/**
 * 🧰 STAGE AUDIO TOOLS — Tuner & Tap Tempo · dB Meter & RTA · Freq Hunter.
 *
 * Pourquoi ce fichier : trois outils de technicien qu'un groupe utilise AVANT
 * la scène et qu'on ne trouve jamais sur un téléphone au bon endroit —
 * accorder une guitare, mesurer le niveau et repérer une fréquence de larsen.
 * Les mettre dans `/games` (déjà 100 % hors-ligne) évite un nouveau menu :
 * c'est le même endroit « on tue le temps utilement ».
 *
 * RÈGLES (elles tiennent tout le fichier) :
 *  - ZÉRO OCTET SUR LE SERVEUR : le micro ne sort JAMAIS du navigateur. Aucun
 *    envoi, aucun stockage d'audio — seulement une analyse en mémoire.
 *  - `getUserMedia` et `AudioContext` sont créés UNIQUEMENT dans un
 *    gestionnaire de clic : iOS/Chrome l'exigent, et jsdom n'a ni l'un ni
 *    l'autre (la page doit se rendre sans Web Audio).
 *  - Aucun libellé en dur : i18n (`games.tuner.*`, `games.rta.*`,
 *    `games.freqhunter.*`).
 *  - La DSP pure est exportée (`detectPitchHz`, `hzToNote`, `ISO_BANDS`) pour
 *    être testée sans navigateur : c'est la seule partie qu'un test peut
 *    prouver, donc c'est celle qu'on isole.
 *
 * RÉFÉRENCE DU LOT : `bandz-frontend/references/ui-roadcase-redesign-2026.md`
 * (section LOT STAGE-TOOLBOX-AND-ARCADE-PLUS).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScoreBar, clamp, useHighScore } from './gameKit';

/* ====================================================================== */
/*  DSP pure — testable sans navigateur                                   */
/* ====================================================================== */

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;

export interface DetectedNote {
  /** Nom de la note la plus proche (`E2`, `A4`…). */
  name: string;
  /** Numéro MIDI arrondi (69 = A4). */
  midi: number;
  /** Écart en cents (−50..+50) entre la mesure et la note la plus proche. */
  cents: number;
}

/**
 * Fréquence → note la plus proche, pour un diapason donné (A4 = `a4` Hz).
 * `cents` mesure l'écart réel : c'est ce que l'aiguille de l'accordeur affiche.
 */
export function hzToNote(hz: number, a4 = 440): DetectedNote | null {
  if (!Number.isFinite(hz) || hz <= 0) return null;
  const midiFloat = 69 + 12 * Math.log2(hz / a4);
  const midi = Math.round(midiFloat);
  if (midi < 0 || midi > 127) return null;
  const cents = (midiFloat - midi) * 100;
  const name = `${NOTE_NAMES[midi % 12]}${Math.floor(midi / 12) - 1}`;
  return { name, midi, cents };
}

/**
 * Autocorrélation temporelle (ACF) avec fenêtre bornée 50 Hz – 2 kHz.
 *
 * Pourquoi pas FFT : sur un signal de guitare, la fondamentale est souvent
 * PLUS FAIBLE que ses harmoniques ; un pic spectral renverrait l'octave
 * supérieure. L'autocorrélation trouve la périodicité, donc la vraie
 * fondamentale — c'est la méthode des accordeurs matériels.
 *
 * Retourne 0 quand le signal est trop faible ou non périodique (silence,
 * bruit de fond, parole) : afficher une note fausse est pire que rien.
 */
export function detectPitchHz(buffer: Float32Array<ArrayBufferLike>, sampleRate: number): number {
  const size = buffer.length;
  if (size < 64 || !Number.isFinite(sampleRate) || sampleRate <= 0) return 0;

  let rms = 0;
  for (let i = 0; i < size; i += 1) rms += buffer[i] * buffer[i];
  rms = Math.sqrt(rms / size);
  if (rms < 0.008) return 0; // silence / souffle : pas de note

  const minLag = Math.max(2, Math.floor(sampleRate / 2000));
  const maxLag = Math.min(Math.floor(sampleRate / 50), size - 2);
  if (maxLag <= minLag) return 0;

  const corr = new Float64Array(maxLag + 2);
  let bestLag = -1;
  let bestCorr = 0;
  for (let lag = minLag; lag <= maxLag; lag += 1) {
    let sum = 0;
    const n = size - lag;
    for (let i = 0; i < n; i += 1) sum += buffer[i] * buffer[i + lag];
    const value = sum / n;
    corr[lag] = value;
    if (value > bestCorr) {
      bestCorr = value;
      bestLag = lag;
    }
  }
  if (bestLag < 0 || bestCorr <= 0) return 0;

  // Anti-erreur d'octave : on garde le PREMIER MAXIMUM LOCAL qui atteint 90 %
  // du pic. Un simple seuil « ≥ 90 % » ne suffit pas : sur une sinusoïde, les
  // retards VOISINS du pic sont eux aussi à plus de 90 % (la corrélation est
  // une courbe lisse) et on retomberait 30 Hz à côté. Il faut donc exiger un
  // vrai sommet local (plus grand que ses deux voisins).
  let lag = bestLag;
  for (let candidate = minLag + 1; candidate < maxLag; candidate += 1) {
    if (
      corr[candidate] >= bestCorr * 0.9 &&
      corr[candidate] > corr[candidate - 1] &&
      corr[candidate] >= corr[candidate + 1]
    ) {
      lag = candidate;
      break;
    }
  }

  // Interpolation parabolique : sans elle, à 44,1 kHz la précision est de
  // ~7 cents à 1 kHz (une corde grave ne tient pas la note).
  const y0 = corr[lag - 1] ?? corr[lag];
  const y1 = corr[lag];
  const y2 = corr[lag + 1] ?? corr[lag];
  const denom = 2 * (2 * y1 - y0 - y2);
  const shift = denom !== 0 ? (y2 - y0) / denom : 0;
  const refined = lag + clamp(shift, -0.5, 0.5);
  const hz = sampleRate / refined;
  return Number.isFinite(hz) && hz >= 40 && hz <= 2100 ? hz : 0;
}

/** 31 bandes ISO 1/3 d'octave — la grille de tout analyseur de spectre pro. */
export const ISO_BANDS: readonly { hz: number; label: string }[] = [
  { hz: 20, label: '20' },
  { hz: 25, label: '25' },
  { hz: 31.5, label: '31' },
  { hz: 40, label: '40' },
  { hz: 50, label: '50' },
  { hz: 63, label: '63' },
  { hz: 80, label: '80' },
  { hz: 100, label: '100' },
  { hz: 125, label: '125' },
  { hz: 160, label: '160' },
  { hz: 200, label: '200' },
  { hz: 250, label: '250' },
  { hz: 315, label: '315' },
  { hz: 400, label: '400' },
  { hz: 500, label: '500' },
  { hz: 630, label: '630' },
  { hz: 800, label: '800' },
  { hz: 1000, label: '1k' },
  { hz: 1250, label: '1.25k' },
  { hz: 1600, label: '1.6k' },
  { hz: 2000, label: '2k' },
  { hz: 2500, label: '2.5k' },
  { hz: 3150, label: '3.15k' },
  { hz: 4000, label: '4k' },
  { hz: 5000, label: '5k' },
  { hz: 6300, label: '6.3k' },
  { hz: 8000, label: '8k' },
  { hz: 10000, label: '10k' },
  { hz: 12500, label: '12.5k' },
  { hz: 16000, label: '16k' },
  { hz: 20000, label: '20k' },
];

/** Les 10 fréquences du quiz Freq Hunter (octaves, 63 Hz → 16 kHz). */
export const QUIZ_FREQS: readonly { hz: number; label: string }[] = [
  { hz: 63, label: '63 Hz' },
  { hz: 125, label: '125 Hz' },
  { hz: 250, label: '250 Hz' },
  { hz: 500, label: '500 Hz' },
  { hz: 1000, label: '1 kHz' },
  { hz: 2000, label: '2 kHz' },
  { hz: 4000, label: '4 kHz' },
  { hz: 8000, label: '8 kHz' },
  { hz: 12000, label: '12 kHz' },
  { hz: 16000, label: '16 kHz' },
];

type AudioContextCtor = typeof AudioContext;

function audioCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as Window & { webkitAudioContext?: AudioContextCtor };
  return window.AudioContext ?? w.webkitAudioContext ?? null;
}

function micAvailable(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}

/* ====================================================================== */
/*  🎸 Tuner & Tap Tempo                                                  */
/* ====================================================================== */

const CALIBRATIONS = [440, 432, 442] as const;
const SIGNATURES = ['4/4', '3/4', '6/8', '5/4', '7/8'] as const;
type Signature = (typeof SIGNATURES)[number];

interface TunerReading {
  hz: number;
  note: DetectedNote | null;
}

export function TunerMetronomePanel() {
  const { t } = useTranslation();

  const [reading, setReading] = useState<TunerReading>({ hz: 0, note: null });
  const [listening, setListening] = useState(false);
  const [calibration, setCalibration] = useState<number>(440);
  const [micError, setMicError] = useState<string | null>(null);

  const [bpm, setBpm] = useState(120);
  const [signature, setSignature] = useState<Signature>('4/4');
  const [metroOn, setMetroOn] = useState(false);
  const [beat, setBeat] = useState(0);

  const ctxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const bufferRef = useRef<Float32Array<ArrayBuffer> | null>(null);
  const calibrationRef = useRef(calibration);
  calibrationRef.current = calibration;

  const metroRef = useRef<{
    timer: number | null;
    nextTime: number;
    beat: number;
    bpm: number;
    signature: Signature;
  }>({ timer: null, nextTime: 0, beat: 0, bpm: 120, signature: '4/4' });
  const tapsRef = useRef<number[]>([]);
  const toneRef = useRef<{ osc: OscillatorNode; gain: GainNode } | null>(null);

  const stopTuner = useCallback(() => {
    if (rafRef.current !== null) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    analyserRef.current = null;
    setListening(false);
    setReading({ hz: 0, note: null });
  }, []);

  const startTuner = useCallback(async () => {
    setMicError(null);
    if (!micAvailable()) {
      setMicError(t('games.tuner.micUnavailable'));
      return;
    }
    const Ctor = audioCtor();
    if (!Ctor) {
      setMicError(t('games.tuner.audioUnavailable'));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
      const ctx = ctxRef.current ?? new Ctor();
      ctxRef.current = ctx;
      if (ctx.state === 'suspended') await ctx.resume();

      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 4096;
      analyser.smoothingTimeConstant = 0;
      source.connect(analyser);

      streamRef.current = stream;
      analyserRef.current = analyser;
      bufferRef.current = new Float32Array(analyser.fftSize);
      setListening(true);

      const loop = () => {
        const node = analyserRef.current;
        const buf = bufferRef.current;
        if (!node || !buf) return;
        node.getFloatTimeDomainData(buf);
        const hz = detectPitchHz(buf, ctx.sampleRate);
        setReading({ hz, note: hz > 0 ? hzToNote(hz, calibrationRef.current) : null });
        rafRef.current = window.requestAnimationFrame(loop);
      };
      rafRef.current = window.requestAnimationFrame(loop);
    } catch {
      setMicError(t('games.tuner.micDenied'));
      setListening(false);
    }
  }, [t]);

  const playReference = useCallback(() => {
    const Ctor = audioCtor();
    if (!Ctor) return;
    const ctx = ctxRef.current ?? new Ctor();
    ctxRef.current = ctx;
    if (ctx.state === 'suspended') void ctx.resume();
    if (toneRef.current) {
      try {
        toneRef.current.osc.stop();
      } catch {
        /* déjà arrêté */
      }
      toneRef.current = null;
    }
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    // Note de référence = le LA du diapason choisi.
    osc.frequency.value = calibration;
    gain.gain.value = 0;
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.setTargetAtTime(0.18, ctx.currentTime, 0.02);
    osc.start();
    toneRef.current = { osc, gain };
    window.setTimeout(() => {
      try {
        gain.gain.setTargetAtTime(0, ctx.currentTime, 0.05);
        osc.stop(ctx.currentTime + 0.25);
      } catch {
        /* déjà arrêté */
      }
    }, 1400);
  }, [calibration]);

  const clicksPerBar = useCallback((sig: Signature) => Number(sig.split('/')[0]), []);

  const scheduleClick = useCallback((ctx: AudioContext, time: number, accent: boolean) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = accent ? 1600 : 1000;
    gain.gain.value = 0;
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(accent ? 0.22 : 0.12, time);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.05);
    osc.start(time);
    osc.stop(time + 0.06);
  }, []);

  const stopMetronome = useCallback(() => {
    const state = metroRef.current;
    if (state.timer !== null) window.clearInterval(state.timer);
    state.timer = null;
    setMetroOn(false);
    setBeat(0);
  }, []);

  const startMetronome = useCallback(() => {
    const Ctor = audioCtor();
    if (!Ctor) return;
    const ctx = ctxRef.current ?? new Ctor();
    ctxRef.current = ctx;
    if (ctx.state === 'suspended') void ctx.resume();

    stopMetronome();
    const state = metroRef.current;
    state.bpm = bpm;
    state.signature = signature;
    state.beat = 0;
    state.nextTime = ctx.currentTime + 0.08;
    setMetroOn(true);

    // Ordonnanceur « lookahead » : Web Audio planifie à l'échantillon près,
    // setInterval ne fait que préparer 100 ms à l'avance. Un setInterval qui
    // joue directement dérive de plusieurs dizaines de ms en 2 minutes.
    state.timer = window.setInterval(() => {
      if (ctx.state === 'closed') return;
      const beats = clicksPerBar(state.signature);
      while (state.nextTime < ctx.currentTime + 0.12) {
        scheduleClick(ctx, state.nextTime, state.beat === 0);
        const beatNow = state.beat;
        const delay = Math.max(0, (state.nextTime - ctx.currentTime) * 1000);
        window.setTimeout(() => setBeat(beatNow), delay);
        state.nextTime += 60 / state.bpm;
        state.beat = (state.beat + 1) % beats;
      }
    }, 25);
  }, [bpm, clicksPerBar, scheduleClick, signature, stopMetronome]);

  const tapTempo = useCallback(() => {
    const now = performance.now();
    const taps = tapsRef.current;
    // Plus de 2 s sans taper = on repart de zéro (ce n'est pas le même tempo).
    if (taps.length > 0 && now - taps[taps.length - 1] > 2000) taps.length = 0;
    taps.push(now);
    if (taps.length > 5) taps.shift();
    if (taps.length < 2) return;
    const intervals: number[] = [];
    for (let i = 1; i < taps.length; i += 1) intervals.push(taps[i] - taps[i - 1]);
    const avg = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    if (avg <= 0) return;
    setBpm(clamp(Math.round(60000 / avg), 40, 240));
  }, []);

  // Le métronome suit le BPM/signature sans être relancé (changement à chaud).
  useEffect(() => {
    metroRef.current.bpm = bpm;
  }, [bpm]);
  useEffect(() => {
    metroRef.current.signature = signature;
  }, [signature]);

  useEffect(
    () => () => {
      if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach((track) => track.stop());
      const state = metroRef.current;
      if (state.timer !== null) window.clearInterval(state.timer);
      try {
        toneRef.current?.osc.stop();
      } catch {
        /* déjà arrêté */
      }
      void ctxRef.current?.close();
      ctxRef.current = null;
    },
    [],
  );

  const note = reading.note;
  const cents = note ? note.cents : 0;
  const inTune = note !== null && Math.abs(cents) <= 5;
  // L'aiguille va de -50 à +50 cents ; on borne pour que l'UI reste lisible.
  const needle = clamp(cents / 50, -1, 1) * 50;

  return (
    <section className="game-panel tool-panel" data-game="tuner" data-game-panel="tuner">
      <div className="tool-card">
        <div className="tool-card-head">{t('games.tuner.tunerTitle')}</div>

        <div className="tuner-readout" data-tuner-readout>
          <span className="tuner-note" data-tuner-note>
            {note ? note.name : '—'}
          </span>
          <span className="tuner-freq">
            {reading.hz > 0 ? `${reading.hz.toFixed(1)} Hz` : `— Hz`}
          </span>
          <span className={`tuner-cents${inTune ? ' ok' : ''}`}>
            {note ? `${cents >= 0 ? '+' : ''}${cents.toFixed(0)} ${t('games.tuner.cents')}` : ''}
          </span>
        </div>

        {/* Jauge : vert au centre, ambre sur les bords. */}
        <div className="tuner-gauge" aria-hidden="true">
          <div className={`tuner-gauge-needle${inTune ? ' ok' : ''}`} style={{ left: `${50 + needle}%` }} />
          <span className="tuner-gauge-zero" />
        </div>

        <div className="tool-row">
          <button
            type="button"
            className="btn btn-primary game-btn"
            data-tuner-start
            onClick={() => (listening ? stopTuner() : void startTuner())}
          >
            {listening ? t('games.tuner.stop') : t('games.tuner.startTuner')}
          </button>

          <label className="tool-field">
            <span>{t('games.tuner.calibration')}</span>
            <select
              value={calibration}
              data-tuner-calibration
              onChange={(event) => setCalibration(Number(event.target.value))}
            >
              {CALIBRATIONS.map((hz) => (
                <option key={hz} value={hz}>
                  {hz} Hz
                </option>
              ))}
            </select>
          </label>

          <button type="button" className="btn game-btn" data-tuner-reference onClick={playReference}>
            🔊 {t('games.tuner.reference')}
          </button>
        </div>

        {micError ? <p className="tool-error">{micError}</p> : null}
        <p className="tool-hint">{t('games.tuner.tunerHint')}</p>
      </div>

      <div className="tool-card">
        <div className="tool-card-head">{t('games.tuner.metronomeTitle')}</div>

        <div className="metro-beats" aria-hidden="true">
          {Array.from({ length: clicksPerBar(signature) }, (_, i) => (
            <span
              key={i}
              className={`metro-dot${metroOn && beat === i ? ' on' : ''}${i === 0 ? ' accent' : ''}`}
            />
          ))}
        </div>

        <div className="tool-row">
          <label className="tool-field tool-field-wide">
            <span>{t('games.tuner.bpm')}</span>
            <input
              type="range"
              min={40}
              max={240}
              step={1}
              value={bpm}
              data-metro-bpm-range
              onChange={(event) => setBpm(Number(event.target.value))}
            />
            <b data-metro-bpm>{bpm}</b>
          </label>

          <label className="tool-field">
            <span>{t('games.tuner.signature')}</span>
            <select
              value={signature}
              data-metro-signature
              onChange={(event) => setSignature(event.target.value as Signature)}
            >
              {SIGNATURES.map((sig) => (
                <option key={sig} value={sig}>
                  {sig}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="tool-row">
          <button
            type="button"
            className="btn btn-primary game-btn"
            data-metro-toggle
            onClick={() => (metroOn ? stopMetronome() : startMetronome())}
          >
            {metroOn ? t('games.tuner.stop') : t('games.tuner.startMetro')}
          </button>
          <button type="button" className="btn game-btn tap-btn" data-tap-tempo onClick={tapTempo}>
            👆 {t('games.tuner.tapTempo')}
          </button>
        </div>

        <p className="tool-hint">{t('games.tuner.tapHint')}</p>
      </div>
    </section>
  );
}

/* ====================================================================== */
/*  📊 dB Meter & 31-band RTA                                             */
/* ====================================================================== */

export function DbMeterRtaPanel() {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [running, setRunning] = useState(false);
  const [frozen, setFrozen] = useState(false);
  const [offset, setOffset] = useState(100);
  const [level, setLevel] = useState({ dbfs: -120, peak: -120, max: -120, spl: 0 });
  const [dominant, setDominant] = useState(0);
  const [micError, setMicError] = useState<string | null>(null);

  const ctxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const freqRef = useRef<Float32Array<ArrayBuffer> | null>(null);
  const timeRef = useRef<Float32Array<ArrayBuffer> | null>(null);
  const rafRef = useRef<number | null>(null);
  const peaksRef = useRef<number[]>(Array.from({ length: ISO_BANDS.length }, () => -100));
  const maxRef = useRef(-120);
  const frozenRef = useRef(false);
  const offsetRef = useRef(offset);
  frozenRef.current = frozen;
  offsetRef.current = offset;

  const draw = useCallback((levels: number[]) => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    const w = cv.width;
    const h = cv.height;
    const floor = -90;
    const ceiling = 0;

    ctx.fillStyle = '#14120f';
    ctx.fillRect(0, 0, w, h);

    const n = ISO_BANDS.length;
    const slot = w / n;
    for (let i = 0; i < n; i += 1) {
      const db = clamp(levels[i] ?? -120, floor, ceiling);
      const frac = (db - floor) / (ceiling - floor);
      const barH = Math.max(1, frac * (h - 22));
      const x = i * slot + 1;
      // Dégradé « console » : vert jusqu'à -12 dB, ambre, puis rouge.
      ctx.fillStyle = db > -6 ? '#e06c6c' : db > -18 ? '#e0a44c' : '#00d47e';
      ctx.fillRect(x, h - 20 - barH, Math.max(1, slot - 2), barH);

      const peak = clamp(peaksRef.current[i] ?? -120, floor, ceiling);
      const peakY = h - 20 - ((peak - floor) / (ceiling - floor)) * (h - 22);
      ctx.fillStyle = 'rgba(245, 196, 105, 0.85)';
      ctx.fillRect(x, peakY, Math.max(1, slot - 2), 2);
    }

    // Axe : 6 repères de fréquence, lisibles sur 320 px.
    ctx.fillStyle = 'rgba(234, 231, 221, 0.45)';
    ctx.font = '8px ui-monospace, monospace';
    ctx.textAlign = 'center';
    for (let i = 0; i < n; i += 5) {
      ctx.fillText(ISO_BANDS[i].label, i * slot + slot / 2, h - 6);
    }
  }, []);

  const stopRta = useCallback(() => {
    if (rafRef.current !== null) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    analyserRef.current = null;
    setRunning(false);
  }, []);

  const loop = useCallback(
    (ctx: AudioContext) => {
      const analyser = analyserRef.current;
      const freq = freqRef.current;
      const time = timeRef.current;
      if (!analyser || !freq || !time) return;

      analyser.getFloatFrequencyData(freq);
      analyser.getFloatTimeDomainData(time);

      if (!frozenRef.current) {
        const binHz = ctx.sampleRate / analyser.fftSize;
        const levels: number[] = [];
        let bestHz = 0;
        let bestDb = -Infinity;
        for (let i = 0; i < ISO_BANDS.length; i += 1) {
          const centre = ISO_BANDS[i].hz;
          const lo = Math.max(1, Math.floor((centre / Math.pow(2, 1 / 6)) / binHz));
          const hi = Math.min(freq.length - 1, Math.ceil((centre * Math.pow(2, 1 / 6)) / binHz));
          let sum = 0;
          let count = 0;
          let localMax = -Infinity;
          for (let b = lo; b <= hi; b += 1) {
            sum += freq[b];
            count += 1;
            if (freq[b] > localMax) localMax = freq[b];
          }
          const db = count > 0 ? sum / count : -120;
          levels.push(db);
          peaksRef.current[i] = Math.max(peaksRef.current[i] - 0.35, db);
        }

        // Fréquence dominante : le pic ABSOLU du spectre (pas la bande).
        for (let b = 1; b < freq.length; b += 1) {
          if (freq[b] > bestDb) {
            bestDb = freq[b];
            bestHz = b * binHz;
          }
        }

        let sumSq = 0;
        for (let i = 0; i < time.length; i += 1) sumSq += time[i] * time[i];
        const rms = Math.sqrt(sumSq / time.length);
        const dbfs = rms > 0 ? Math.max(-120, 20 * Math.log10(rms)) : -120;
        maxRef.current = Math.max(maxRef.current, dbfs);
        const peak = 20 * Math.log10(Math.max(1e-6, Math.max(...Array.from(time, Math.abs))));

        draw(levels);
        setLevel({
          dbfs,
          peak,
          max: maxRef.current,
          spl: dbfs + offsetRef.current,
        });
        setDominant(bestDb > -100 ? bestHz : 0);
      }

      rafRef.current = window.requestAnimationFrame(() => loop(ctx));
    },
    [draw],
  );

  const startRta = useCallback(async () => {
    setMicError(null);
    if (!micAvailable()) {
      setMicError(t('games.rta.micUnavailable'));
      return;
    }
    const Ctor = audioCtor();
    if (!Ctor) {
      setMicError(t('games.rta.audioUnavailable'));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
      const ctx = ctxRef.current ?? new Ctor();
      ctxRef.current = ctx;
      if (ctx.state === 'suspended') await ctx.resume();

      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 4096;
      analyser.smoothingTimeConstant = 0.72;
      analyser.minDecibels = -110;
      source.connect(analyser);

      streamRef.current = stream;
      analyserRef.current = analyser;
      freqRef.current = new Float32Array(analyser.frequencyBinCount);
      timeRef.current = new Float32Array(analyser.fftSize);
      peaksRef.current = Array.from({ length: ISO_BANDS.length }, () => -100);
      maxRef.current = -120;
      setRunning(true);
      rafRef.current = window.requestAnimationFrame(() => loop(ctx));
    } catch {
      setMicError(t('games.rta.micDenied'));
      setRunning(false);
    }
  }, [loop, t]);

  useEffect(
    () => () => {
      if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach((track) => track.stop());
      void ctxRef.current?.close();
      ctxRef.current = null;
    },
    [],
  );

  return (
    <section className="game-panel tool-panel" data-game="rta" data-game-panel="rta">
      <div className="tool-card">
        <div className="tool-card-head">{t('games.rta.title')}</div>

        <div className="rta-readout">
          <div className="rta-big">
            <span className="rta-value" data-rta-spl>
              {level.dbfs > -120 ? `${(level.dbfs + offset).toFixed(1)}` : '—'}
            </span>
            <span className="rta-unit">dB SPL</span>
          </div>
          <div className="rta-small">
            <span>
              {t('games.rta.level')} <b data-rta-dbfs>{level.dbfs.toFixed(1)} dBFS</b>
            </span>
            <span>
              {t('games.rta.peak')} <b data-rta-peak>{level.peak.toFixed(1)}</b>
            </span>
            <span>
              {t('games.rta.max')} <b data-rta-max>{level.max.toFixed(1)}</b>
            </span>
          </div>
          <div className="rta-big rta-dominant">
            <span className="rta-value" data-rta-dominant>
              {dominant > 0 ? (dominant >= 1000 ? `${(dominant / 1000).toFixed(2)}k` : dominant.toFixed(0)) : '—'}
            </span>
            <span className="rta-unit">{t('games.rta.dominant')}</span>
          </div>
        </div>

        <div className="game-stage rta-stage">
          <canvas
            ref={canvasRef}
            width={320}
            height={180}
            className="game-canvas game-canvas-rta"
            role="img"
            aria-label={t('games.rta.title')}
          />
        </div>

        <div className="tool-row">
          <button
            type="button"
            className="btn btn-primary game-btn"
            data-rta-start
            onClick={() => (running ? stopRta() : void startRta())}
          >
            {running ? t('games.rta.stop') : t('games.rta.start')}
          </button>

          <button
            type="button"
            className={`btn game-btn${frozen ? ' on' : ''}`}
            data-rta-freeze
            aria-pressed={frozen}
            onClick={() => setFrozen((v) => !v)}
          >
            ❄️ {t('games.rta.freeze')}
          </button>

          <label className="tool-field tool-field-wide">
            <span>{t('games.rta.calibration')}</span>
            <input
              type="range"
              min={70}
              max={130}
              step={1}
              value={offset}
              data-rta-offset
              onChange={(event) => setOffset(Number(event.target.value))}
            />
            <b>+{offset}</b>
          </label>
        </div>

        {micError ? <p className="tool-error">{micError}</p> : null}
        <p className="tool-hint">{t('games.rta.hint')}</p>
      </div>
    </section>
  );
}

/* ====================================================================== */
/*  👂 Freq Hunter — quiz d'oreille FOH / moniteur                        */
/* ====================================================================== */

/** Bruit rose (filtre de Paul Kellet) : le signal d'entraînement des techs. */
function fillPinkNoise(data: Float32Array): void {
  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  let b3 = 0;
  let b4 = 0;
  let b5 = 0;
  let b6 = 0;
  for (let i = 0; i < data.length; i += 1) {
    const white = Math.random() * 2 - 1;
    b0 = 0.99886 * b0 + white * 0.0555179;
    b1 = 0.99332 * b1 + white * 0.0750759;
    b2 = 0.969 * b2 + white * 0.153852;
    b3 = 0.8665 * b3 + white * 0.3104856;
    b4 = 0.55 * b4 + white * 0.5329522;
    b5 = -0.7616 * b5 - white * 0.016898;
    data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
    b6 = white * 0.115926;
  }
}

export function FreqHunterGame() {
  const { t } = useTranslation();

  const [target, setTarget] = useState<number>(1000);
  const [guess, setGuess] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestScore, bumpBest] = useHighScore('bandz.games.high.freqhunter');
  const [playing, setPlaying] = useState(false);
  const [bypassed, setBypassed] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);

  const ctxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const filterRef = useRef<BiquadFilterNode | null>(null);
  const scoreRef = useRef(0);
  const streakRef = useRef(0);

  const stopAudio = useCallback(() => {
    try {
      sourceRef.current?.stop();
    } catch {
      /* déjà arrêté */
    }
    sourceRef.current = null;
    filterRef.current = null;
    setPlaying(false);
    setBypassed(false);
  }, []);

  const playFeedback = useCallback(
    (bypass: boolean) => {
      const Ctor = audioCtor();
      if (!Ctor) {
        setAudioError(t('games.freqhunter.audioUnavailable'));
        return;
      }
      const ctx = ctxRef.current ?? new Ctor();
      ctxRef.current = ctx;
      if (ctx.state === 'suspended') void ctx.resume();

      try {
        sourceRef.current?.stop();
      } catch {
        /* déjà arrêté */
      }

      // 2 s de bruit rose en boucle : le larsen simulé est stable, donc
      // comparable avec/sans le boost (c'est tout l'exercice).
      const buffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
      fillPinkNoise(buffer.getChannelData(0));

      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;

      const filter = ctx.createBiquadFilter();
      filter.type = 'peaking';
      filter.frequency.value = target;
      filter.Q.value = 4;
      filter.gain.value = bypass ? 0 : 12;

      const out = ctx.createGain();
      out.gain.value = 0.9;
      source.connect(filter);
      filter.connect(out);
      out.connect(ctx.destination);
      source.start();

      sourceRef.current = source;
      filterRef.current = filter;
      setPlaying(true);
      setBypassed(bypass);
      setAudioError(null);
    },
    [t, target],
  );

  const toggleBypass = useCallback(() => {
    const filter = filterRef.current;
    if (!filter) {
      playFeedback(true);
      return;
    }
    const next = !bypassed;
    filter.gain.value = next ? 0 : 12;
    setBypassed(next);
  }, [bypassed, playFeedback]);

  const nextRound = useCallback(() => {
    stopAudio();
    const pool = QUIZ_FREQS.filter((f) => f.hz !== target);
    const pick = pool[Math.floor(Math.random() * pool.length)];
    setTarget(pick?.hz ?? 1000);
    setGuess(null);
  }, [stopAudio, target]);

  const answer = useCallback(
    (hz: number) => {
      if (guess !== null) return;
      setGuess(hz);
      if (hz === target) {
        scoreRef.current += 1;
        streakRef.current += 1;
        setScore(scoreRef.current);
        setStreak(streakRef.current);
        bumpBest(scoreRef.current);
      } else {
        streakRef.current = 0;
        setStreak(0);
      }
    },
    [bumpBest, guess, target],
  );

  useEffect(
    () => () => {
      try {
        sourceRef.current?.stop();
      } catch {
        /* déjà arrêté */
      }
      void ctxRef.current?.close();
      ctxRef.current = null;
    },
    [],
  );

  const correct = guess !== null && guess === target;
  const revealed = guess !== null;

  return (
    <section className="game-panel tool-panel" data-game="freqhunter" data-game-panel="freqhunter">
      <ScoreBar
        score={score}
        best={bestScore}
        status="idle"
        onToggle={() => playFeedback(false)}
        onRestart={() => {
          scoreRef.current = 0;
          streakRef.current = 0;
          setScore(0);
          setStreak(0);
          nextRound();
        }}
        extra={`${t('games.freqhunter.streak')} ${streak}`}
      />

      <div className="tool-card">
        <div className="tool-card-head">{t('games.freqhunter.title')}</div>

        <div className="tool-row">
          <button
            type="button"
            className="btn btn-primary game-btn"
            data-freqhunter-play
            onClick={() => playFeedback(false)}
          >
            🔊 {t('games.freqhunter.play')}
          </button>
          <button
            type="button"
            className={`btn game-btn${bypassed ? ' on' : ''}`}
            data-freqhunter-bypass
            aria-pressed={bypassed}
            onClick={toggleBypass}
          >
            {t('games.freqhunter.bypass')}
          </button>
          <button type="button" className="btn game-btn" data-freqhunter-stop onClick={stopAudio}>
            ⏹ {t('games.freqhunter.stop')}
          </button>
        </div>

        {audioError ? <p className="tool-error">{audioError}</p> : null}

        <p className="tool-hint">{playing ? t('games.freqhunter.listening') : t('games.freqhunter.hint')}</p>

        <div className="freq-grid" role="group" aria-label={t('games.freqhunter.title')}>
          {QUIZ_FREQS.map((f) => {
            const isGuess = guess === f.hz;
            const isTarget = revealed && f.hz === target;
            const state = isTarget ? ' ok' : isGuess ? ' bad' : '';
            return (
              <button
                key={f.hz}
                type="button"
                className={`game-ctl freq-btn${state}`}
                data-freq-guess={f.hz}
                aria-pressed={isGuess}
                disabled={revealed}
                onClick={() => answer(f.hz)}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {revealed ? (
          <div className="freq-result" data-freq-result={correct ? 'correct' : 'wrong'}>
            <p>
              {correct ? t('games.freqhunter.correct') : t('games.freqhunter.wrong')}{' '}
              <b>{QUIZ_FREQS.find((f) => f.hz === target)?.label}</b>
            </p>
            <button type="button" className="btn btn-primary game-btn" data-freqhunter-next onClick={nextRound}>
              {t('games.freqhunter.next')}
            </button>
          </div>
        ) : null}

        <p className="game-desc">{t('games.freqhunter.desc')}</p>
      </div>
    </section>
  );
}
